import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useSubNavPlacement } from './useWideViewport';

// Module-level shared state (singleton)
const sidebarOpen = ref(false); // overlay drawer (portrait / phone)
const desktopSidebarOpen = ref(true); // the docked column's expanded state (landscape/desktop only)
const isSettingsActive = ref(false);

/**
 * Is the settings tree DOCKED beside the content? Same rule as the rooms rail
 * (card t_d9971280): the settings tree is a COLUMN only where the shell docks
 * its own section navigation in a left column, and the off-canvas drawer
 * everywhere else — portrait included, where `innerWidth >= 768` used to be
 * read as "desktop" and left a 224px column beside the portrait bottom bar.
 */
const isDockedColumn = computed(() => useSubNavPlacement().value === 'column');

const allItems = [
  { path: '/settings/integrations', label: 'Integrations' },
  { path: '/settings/rooms', label: 'Rooms' },
  { path: '/settings/devices', label: 'Devices' },
  { path: '/settings/scenes', label: 'Scenes' },
  { path: '/settings/routines', label: 'Routines' },
  { path: '/settings/layouts', label: 'Layouts' },
  { path: '/settings/model', label: '3D Model' },
  { path: '/settings/system', label: 'System' },
  { path: '/settings/general', label: 'General' },
  { path: '/settings/users', label: 'Users' },
  { path: '/settings/agent', label: 'Agent' },
  { path: '/settings/demo', label: 'Demo' },
  { path: '/settings/backup', label: 'Backup' },
  { path: '/settings/gallery', label: 'Gallery' },
  { path: '/settings/logs', label: 'Logs' },
  { path: '/settings/danger', label: 'Danger Zone' },
  { path: '/settings/streaming', label: 'Streaming' },
];

export function useSettingsNav() {
  const route = useRoute();

  const normalizedPath = computed(() => route.path.replace(/^\/h\/[^/]+/, ''));

  const currentPageLabel = computed(() => {
    const current = allItems.find((item) => normalizedPath.value.startsWith(item.path));
    return current?.label ?? 'Settings';
  });

  function toggleSidebar() {
    if (isDockedColumn.value) {
      desktopSidebarOpen.value = !desktopSidebarOpen.value;
    } else {
      sidebarOpen.value = !sidebarOpen.value;
    }
  }

  // Open (never close) — used by the main-nav "Settings" item so clicking it
  // while already on a settings page can't leave the settings sidebar hidden.
  // Closing remains the dedicated top-bar toggle's job.
  function openSidebar() {
    if (isDockedColumn.value) {
      desktopSidebarOpen.value = true;
    } else {
      sidebarOpen.value = true;
    }
  }

  function closeSidebar() {
    sidebarOpen.value = false;
  }

  function setSettingsActive(active: boolean) {
    isSettingsActive.value = active;
    if (!active) sidebarOpen.value = false;
  }

  return {
    sidebarOpen,
    desktopSidebarOpen,
    isDockedColumn,
    isSettingsActive,
    currentPageLabel,
    toggleSidebar,
    openSidebar,
    closeSidebar,
    setSettingsActive,
  };
}
